/**
 * ComingSoonTemplate — Reusable Coming Soon page template for sports.
 * Each sport page passes its i18n keys, badge style, SVG preview, and variant (classic/ai).
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SeoHead } from "../components/common/SeoHead.tsx";
import { useI18n } from "../i18n/context.tsx";
import styles from "./ComingSoon.module.css";

interface ComingSoonTemplateProps {
  sport: {
    title: string;
    subtitle: string;
    classicTitle: string;
    classicDesc: string;
    aiTitle: string;
    aiDesc: string;
  };
  variant: "classic" | "ai";
  badgeClass: string;
  svgPreview?: ReactNode;
  seoTitle: string;
  seoDesc: string;
  canonicalPath: string;
}

export function ComingSoonTemplate({
  sport,
  variant,
  badgeClass,
  svgPreview,
  seoTitle,
  seoDesc,
  canonicalPath,
}: ComingSoonTemplateProps) {
  const { t, localePath } = useI18n();

  const isClassic = variant === "classic";
  const activeTitle = isClassic ? sport.classicTitle : sport.aiTitle;
  const activeDesc = isClassic ? sport.classicDesc : sport.aiDesc;

  return (
    <>
      <SeoHead title={seoTitle} description={seoDesc} path={canonicalPath} />
      <div className={styles.page}>
        <span className={`${styles.badge} ${badgeClass}`}>{t.comingSoon.badge}</span>
        <h1 className={styles.title}>{sport.title}</h1>
        <p className={styles.subtitle}>{sport.subtitle}</p>

        {svgPreview && <div className={styles.svgPreview}>{svgPreview}</div>}

        <div className={styles.cards}>
          <div className={`${styles.card} ${isClassic ? styles.cardClassic : styles.cardAi}`}>
            <span
              className={`${styles.cardLabel} ${isClassic ? styles.classicLabel : styles.aiLabel}`}
            >
              {isClassic ? t.home.classicBadge : t.home.aiBadge}
            </span>
            <h3 className={styles.cardTitle}>{activeTitle}</h3>
            <p className={styles.cardDesc}>{activeDesc}</p>
            <p className={styles.cardPrice}>{isClassic ? t.home.free : t.home.aiPrice}</p>
          </div>
        </div>

        <span className={styles.comingSoon}>{t.comingSoon.inDevelopment}</span>
        <Link to={localePath("/basketball/classic")} className={styles.backLink}>
          {t.comingSoon.backToBasketball}
        </Link>
      </div>
    </>
  );
}
